import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Plus, Trash2, Crown, User, X, Save } from 'lucide-react';

const Admins = () => {
  const { isSuperAdmin } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'admin'
  });

  useEffect(() => {
    if (isSuperAdmin()) {
      loadAdmins();
    }
  }, []);

  const loadAdmins = async () => {
    try {
      const data = await adminsAPI.getAll();
      setAdmins(data);
    } catch (error) {
      console.error('Failed to load admins:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await adminsAPI.create(formData);
      loadAdmins();
      closeModal();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet administrateur ?')) return;
    
    try {
      await adminsAPI.delete(id);
      loadAdmins();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setFormData({ email: '', full_name: '', password: '', role: 'admin' });
  };

  if (!isSuperAdmin()) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-white mb-4">Accès Refusé</h2>
          <p className="text-gray-400">Cette page est réservée au super administrateur.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">Administrateurs</h1>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 bg-cyan-500 text-white px-4 py-2 rounded-lg hover:bg-cyan-600 transition"
          >
            <Plus className="w-5 h-5" />
            Nouvel Admin
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-cyan-400 border-t-transparent"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {admins.map((admin) => (
              <div key={admin.id} className="bg-blue-800 rounded-lg p-6 border border-blue-700">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      admin.role === 'super_admin' ? 'bg-yellow-500' : 'bg-cyan-500'
                    }`}>
                      {admin.role === 'super_admin' ? (
                        <Crown className="w-6 h-6 text-white" />
                      ) : (
                        <User className="w-6 h-6 text-white" />
                      )}
                    </div>
                    <div>
                      <div className="text-white font-bold">{admin.full_name}</div>
                      <div className="text-xs text-cyan-400">
                        {admin.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                      </div>
                    </div>
                  </div>
                  {admin.role !== 'super_admin' && (
                    <button
                      onClick={() => handleDelete(admin.id)}
                      className="p-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="text-gray-400 text-sm">{admin.email}</div>
                <div className="mt-3 text-xs text-gray-500">
                  Créé le {new Date(admin.created_at).toLocaleDateString('fr-FR')}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-blue-800 rounded-lg p-6 max-w-md w-full border border-blue-700">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">Nouvel Administrateur</h2>
                <button onClick={closeModal} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-white mb-2">Nom complet *</label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required
                    className="w-full bg-blue-700 border border-blue-600 rounded px-4 py-2 text-white"
                  />
                </div>

                <div>
                  <label className="block text-white mb-2">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full bg-blue-700 border border-blue-600 rounded px-4 py-2 text-white"
                  />
                </div>

                <div>
                  <label className="block text-white mb-2">Mot de passe *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={8}
                    className="w-full bg-blue-700 border border-blue-600 rounded px-4 py-2 text-white"
                  />
                  <p className="text-gray-400 text-xs mt-1">Minimum 8 caractères</p>
                </div>

                <div>
                  <label className="block text-white mb-2">Rôle</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="w-full bg-blue-700 border border-blue-600 rounded px-4 py-2 text-white"
                  >
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 flex items-center justify-center gap-2 bg-cyan-500 text-white py-2 rounded hover:bg-cyan-600 transition"
                  >
                    <Save className="w-5 h-5" />
                    Créer
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 bg-gray-600 text-white py-2 rounded hover:bg-gray-700 transition"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Admins;